import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { InventoryItemTypeModal } from '../components/InventoryItemTypeModal';
import { inventoryItemTypesApi } from '../inventoryApi';
import type { InventoryItemTypeListItem } from '../inventoryTypes';

/**
 * The governed vocabulary of item names.
 *
 * It exists so a name is standardized, searchable and correctable in ONE place — left as free
 * text on the item, one thing ends up recorded four ways and no report can group them. Every
 * item holds a foreign key here and resolves the name on every read, so correcting a word
 * corrects every item drawn from it.
 */
export function InventoryItemTypesIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<InventoryItemTypeListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    itemType: InventoryItemTypeListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchItemTypes = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      inventoryItemTypesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (itemType: InventoryItemTypeListItem): MenuItem[] => [
      {
        key: `edit-${itemType.id}`,
        label: 'Edit',
        permission: 'inventory-item-types.update',
        onClick: () => setEditing(itemType),
      },
      {
        key: `restore-${itemType.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'inventory-item-types.delete',
        disabledReason: itemType.isActive ? 'This item type is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', itemType }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await inventoryItemTypesApi.deactivate(confirming.itemType.id);
      else await inventoryItemTypesApi.activate(confirming.itemType.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<InventoryItemTypeListItem>
        module="inventory-item-types"
        breadcrumb={[
          'Logistics',
          'Inventory',
          { label: 'Settings', to: '/inventory/settings' },
          'Item Types',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['InventoryItemType']}
        rowKey={(itemType) => itemType.id}
        fetch={fetchItemTypes}
        reloadKey={reloadKey}
        searchPlaceholder="Search item types or their category"
        emptyMessage="No item types yet. Add the canonical names items are drawn from."
        onCreate={() => setCreating(true)}
        createPermission="inventory-item-types.create"
        onRowOpen={(itemType) => navigate(`/inventory/settings/item-types/${itemType.id}`)}
        openPermission="inventory-item-types.read"
        rowActions={rowActions}
        onDelete={(itemType) => setConfirming({ kind: 'deactivate', itemType })}
        deletePermission="inventory-item-types.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(itemType) =>
          itemType.isActive ? null : 'This item type is already deleted.'
        }
        onClearData={() => inventoryItemTypesApi.clear()}
        clearDataPermission="inventory-item-types.clear-data"
        otherInfo={(itemType) => itemType}
      />

      {creating || editing !== null ? (
        <InventoryItemTypeModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete item type' : 'Restore item type'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`inventory-item-type-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.itemType.name}" stops being offered on the item form.`
                  : `"${confirming.itemType.name}" is offered again on the item form.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.itemType.itemCount > 0 ? (
                <p>
                  {confirming.itemType.itemCount} item
                  {confirming.itemType.itemCount === 1 ? '' : 's'} still
                  {confirming.itemType.itemCount === 1 ? ' carries' : ' carry'} this name, and keep
                  resolving it. Retiring a word stops it being offered; it does not rewrite what an
                  existing record says it is.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted item type can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<InventoryItemTypeListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 300,
    flex: true,
    sortable: true,
    render: (itemType) => <TextCell value={itemType.name} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    summary: true,
    render: (itemType) => <TextCell value={itemType.description} />,
  },
  {
    /*
      Null is the deliberate DEFAULT here, not an absence — a name that spans categories is
      offered under all of them — so it renders as a sentence rather than an em dash, which
      would read as a decision nobody made.
    */
    key: 'category',
    header: 'Offered under',
    width: 200,
    sortable: true,
    summary: true,
    render: (itemType) => (
      <TextCell value={itemType.inventoryCategoryName ?? 'Every category'} />
    ),
  },
  {
    key: 'itemCount',
    header: 'Items',
    width: 90,
    align: 'right',
    sortable: true,
    render: (itemType) => <TextCell value={String(itemType.itemCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (itemType) =>
      itemType.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
